import React from 'react';
import { Bot, X, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import Markdown from 'react-markdown';
import { REASON_CODE_LABELS, needsManualReview, stripManualReviewPrefix } from './prediction';

// Shows one Predict response: status, confidence and reason.
const PredictionModal = ({ isOpen, onClose, data }) => {
    if (!isOpen || !data) return null;

    const statusMap = { 'Accepted': 'Accepted', 'Rejected': 'Rejected' };
    const rawReason = typeof data.reason === 'string' ? data.reason : '';
    const isManualReview = needsManualReview(data);
    const status = isManualReview ? 'Manual Review Required' : (statusMap[data.warranty_status] || "N/A");
    const reason = isManualReview
        ? (stripManualReviewPrefix(rawReason) || "No reason provided.")
        : (REASON_CODE_LABELS.get(data.reason) || data.reason || "No reason provided.");
    const statusProb = (data.warranty_status_probability || 0) * 100;

    const isAccepted = status === 'Accepted';

    // Dynamic styling based on status
    const statusColorClass = isAccepted ? 'text-green-500' : (isManualReview ? 'text-amber-600' : 'text-red-500');
    const barColorClass = isAccepted ? 'bg-green-600' : (isManualReview ? 'bg-amber-600' : 'bg-red-600');
    const confidenceLabel = isAccepted ? "Acceptance Confidence" : (isManualReview ? "Assessment Confidence" : "Rejection Confidence");

    const headerIconColor = 'text-text-main';
    const statusDisplayClass = `bg-transparent border border-line ${statusColorClass}`;
    const reasonBorderClass = 'border-l-2 border-text-main';
    const StatusIcon = isAccepted ? CheckCircle2 : (isManualReview ? AlertTriangle : XCircle);

    return (
        <div className="fixed inset-0 bg-primary bg-opacity-80 backdrop-blur-sm flex justify-center items-center z-40" onClick={onClose}>
            <div className="bg-surface rounded-none border border-line w-full max-w-2xl mx-4 flex flex-col" onClick={e => e.stopPropagation()}>
                <header className="flex justify-between items-center p-6 border-b border-line">
                    <div className="flex items-center space-x-4">
                        <Bot className={`h-6 w-6 ${headerIconColor}`} />
                        <h5 className="text-sm font-semibold text-text-main uppercase tracking-widest">AI Claim Prediction</h5>
                    </div>
                    <button onClick={onClose} className="p-2 text-text-muted hover:text-text-main transition-colors">
                        <X className="h-5 w-5" />
                    </button>
                </header>

                <div className="p-8 space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                        <div>
                            <label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-3">Predicted Status</label>
                            <div className={`flex items-center justify-center space-x-3 p-4 font-semibold text-sm uppercase tracking-widest ${statusDisplayClass}`}>
                                <StatusIcon className="h-5 w-5" />
                                <span>{status}</span>
                            </div>
                            {isManualReview && (
                                <p className="mt-2 text-xs text-text-muted tracking-wide">
                                    Not automatically approved and not a final rejection: a reviewer must decide this claim.
                                </p>
                            )}
                        </div>
                        <div>
                            {/* Updated Confidence Section */}
                            <label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-3">
                                {confidenceLabel}
                            </label>
                            <div className="w-full bg-background border border-line h-12 overflow-hidden relative">
                                <div
                                    className={`h-full flex items-center ${barColorClass} justify-center text-white font-semibold text-xs tracking-widest transition-all duration-500`}
                                    style={{ width: `${statusProb.toFixed(1)}%` }}
                                >
                                    {statusProb.toFixed(1)}%
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-3">
                            Predicted Reason
                        </label>
                        <div className={`
                                bg-background p-6 ${reasonBorderClass}
                                max-h-[300px] overflow-y-auto
                                [&::-webkit-scrollbar]:w-2
                                [&::-webkit-scrollbar-track]:bg-transparent
                                [&::-webkit-scrollbar-thumb]:bg-line
                                hover:[&::-webkit-scrollbar-thumb]:bg-text-muted
                                [&::-webkit-scrollbar-thumb]:rounded-full
                            `}>
                            <div className="text-sm text-text-main leading-relaxed">
                                <Markdown>
                                    {reason}
                                </Markdown>
                            </div>
                        </div>
                    </div>
                </div>

                <footer className="p-6 border-t border-line bg-transparent flex justify-end">
                    <button
                        onClick={onClose}
                        className="btn btn-primary"
                    >
                        Close
                    </button>
                </footer>
            </div>
        </div>
    );
};

export default PredictionModal;
